import type { EChartsOption } from 'echarts'

import { type PanelRow } from '../../payload/panels'
import { bucketLabel, num, shortDate } from '../format'
import { chartTheme as theme } from './chartTheme'

type Grain = 'day' | 'week'

const valueOf = (row: PanelRow, key: string): number | null =>
  row[key] === null || row[key] === undefined ? null : Number(row[key])

const axes = (
  rows: PanelRow[],
  xKey: string
): Pick<EChartsOption, 'xAxis' | 'yAxis' | 'grid' | 'textStyle'> => ({
  textStyle: { fontFamily: 'Inter', fontSize: theme.fontSize },
  grid: { top: 24, left: 0, right: 8, bottom: 0, containLabel: true },
  xAxis: {
    type: 'category',
    data: rows.map(row => shortDate(row[xKey])),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: theme.axis.lineColor } },
    axisLabel: { color: theme.axis.labelColor, fontSize: theme.fontSize },
  },
  yAxis: {
    type: 'value',
    min: 0,
    splitNumber: 3,
    splitLine: {
      lineStyle: { color: theme.axis.splitLineColor, type: 'dashed' },
    },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: theme.axis.labelColor,
      fontSize: theme.fontSize,
      formatter: (v: number) => num(v),
    },
  },
})

type TooltipParam = {
  dataIndex?: number
  marker?: string
  seriesName?: string
  value?: unknown
}

const tooltip = (
  rows: PanelRow[],
  xKey: string,
  grain: Grain
): EChartsOption['tooltip'] => ({
  trigger: 'axis',
  confine: true,
  backgroundColor: theme.tooltip.backgroundColor,
  borderWidth: 0,
  textStyle: { color: theme.tooltip.textColor, fontSize: theme.fontSize },
  formatter: (params): string => {
    const items = (Array.isArray(params) ? params : [params]) as TooltipParam[]
    const index = items[0]?.dataIndex ?? 0
    const row = rows[index] ?? {}
    const title =
      row.is_loaded === false
        ? `${bucketLabel(row, xKey, grain)} — not imported yet`
        : bucketLabel(row, xKey, grain)
    const lines = items.map(
      p => `${p.marker ?? ''} ${p.seriesName ?? ''}: ${num(p.value)}`
    )
    return [title, ...lines].join('<br/>')
  },
})

export type BarsSeries = { key: string; label: string; color: string }

/** Adjacent bars, never stacked: overlapping populations must not imply a sum. */
export const buildBarsOption = ({
  rows,
  xKey,
  grain,
  series,
  label,
}: {
  rows: PanelRow[]
  xKey: string
  grain: Grain
  series: BarsSeries[]
  label: string
}): EChartsOption => ({
  ...axes(rows, xKey),
  aria: { enabled: true, label: { description: label } },
  legend:
    series.length > 1
      ? {
          top: 0,
          right: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: {
            color: theme.legend.textColor,
            fontSize: theme.fontSize,
          },
        }
      : undefined,
  tooltip: tooltip(rows, xKey, grain),
  series: series.map(sr => ({
    type: 'bar',
    name: sr.label,
    data: rows.map(row => valueOf(row, sr.key)),
    itemStyle: { color: sr.color, borderRadius: [2, 2, 0, 0] },
    barMaxWidth: 22,
  })),
})

/** A small multiple: one metric, one line, gaps where a day is not imported. */
export const buildSparkOption = ({
  rows,
  xKey,
  yKey,
  title,
  grain,
}: {
  rows: PanelRow[]
  xKey: string
  yKey: string
  title: string
  grain: Grain
}): EChartsOption => ({
  ...axes(rows, xKey),
  aria: { enabled: true, label: { description: title } },
  tooltip: tooltip(rows, xKey, grain),
  series: [
    {
      type: 'line',
      name: title,
      data: rows.map(row => valueOf(row, yKey)),
      connectNulls: false,
      showSymbol: rows.length <= 31,
      symbolSize: 5,
      lineStyle: { width: 2, color: theme.series[0] },
      itemStyle: { color: theme.series[0] },
    },
  ],
})
